import { FC, Fragment, useState, useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { camelToSnake } from "caseparser";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import { useStatesQuery, useTimezonesQuery } from "@/app/app.apiStates";
import { BranchDeleteForm } from "../organisms/BranchDeleteForm";
import { BranchSideBar } from "../organisms/BranchSideBar";
import { useCountriesQuery } from "@/app/app.apiCountry";
import { BranchForm } from "../organisms/BranchForm";
import { useGymsQuery } from "@/app/gyms/gyms.api";
import { selectUser } from "@/app/auth/auth.slice";

import {
  useDeleteBranchMutation,
  useUpdateBranchNewMutation,
} from "@/app/branchs/branchs.api";

import { useGetBranchQuery } from "@/app/branchs/branchs.api";
import { useAppDispatch } from "@/app/app.store"; // Importa el hook useAppDispatch
import { setBranch, clearBranch } from "@/app/branchs/branch.slice"; // Importa las acciones
import { toasts } from "@/shared/utils/toasts";

const toSnakeCase = (obj: any) => {
  const newObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      newObj[newKey] = obj[key];
    }
  }
  return newObj;
};

export const BranchLayout: FC<any> = () => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch(); // Usa el hook useAppDispatch
  const [branchFormVisible, setBranchFormVisible] = useState(false);
  const [branchDeleteFormVisible, setBranchDeleteFormVisible] = useState(false);

  const gymsQuery = useGymsQuery({
    branchId: user?.branch?.id,
  });
  const countriesQuery = useCountriesQuery(undefined);
  const statesQuery = useStatesQuery(undefined);
  const timezonesQuery = useTimezonesQuery(undefined);
  const branchQuery = useGetBranchQuery({ branchId: id });

  const [deleteBranch] = useDeleteBranchMutation();

  const [updateBranch, updateBranchResult] = useUpdateBranchNewMutation();

  useEffect(() => {
    if (branchQuery.isSuccess && branchQuery.data) {
      dispatch(setBranch(camelToSnake(branchQuery.data)));
    } else {
      dispatch(clearBranch());
    }
  }, [branchQuery.isSuccess, branchQuery.data, dispatch]);

  const handleUpdateBranch = async (values: any) => {
    try {
      const formToSend = new FormData();
      const largeLogo =
        typeof values.large_logo === "string" &&
        values.large_logo.includes("http");
      const logo =
        typeof values.profile_avatar === "string" &&
        values.profile_avatar.includes("http");

      formToSend.append("gym_id", values.gym_id);
      formToSend.append("branch", values.branch);

      if (logo) {
        formToSend.append(
          "profile_avatar",
          (values.profile_avatar as string).split("/").pop()
        );
      } else {
        formToSend.append("profile_avatar", values.profile_avatar);
      }
      if (largeLogo) {
        formToSend.append(
          "large_logo",
          (values.large_logo as string).split("/").pop()
        );
      } else {
        formToSend.append("large_logo", values.large_logo); //file
      }
      formToSend.append("first_color", values.first_color);
      formToSend.append("secound_color", values.secound_color);
      formToSend.append("video_tutorial", values.video_tutorial);
      formToSend.append("phone", values.phone);
      formToSend.append("email", values.email);
      formToSend.append("country", values.country);
      formToSend.append("country_code", values.countryCode);
      formToSend.append("state", values.state);
      formToSend.append("state_code", values.stateCode);
      formToSend.append("population", values.population);
      formToSend.append("adress", values.address);
      formToSend.append("code_zip", values.code_zip);
      formToSend.append("timezone", values.timezone);
      formToSend.append("app_ios", values.app_ios);
      formToSend.append("app_android", values.app_android);

      if (values.is_chat) {
        formToSend.append("is_chat", "on");
      } else {
        formToSend.append("is_chat", "off");
      }
      formToSend.append("sets", String(values.sets));

      if (values.block_training) {
        formToSend.append("block_training", "on");
      } else {
        formToSend.append("block_training", "off");
      }

      if (values.block_nutrition) {
        formToSend.append("block_nutrition", "on");
      } else {
        formToSend.append("block_nutrition", "off");
      }

      if (values.compliance_schedule) {
        formToSend.append("compliance_schedule", "on");
      } else {
        formToSend.append("compliance_schedule", "off");
      }

      if (values.customer_to_gym_subscription) {
        formToSend.append("customer_to_gym_subscription", "on");
      } else {
        formToSend.append("customer_to_gym_subscription", "off");
      }

      if (values.multi_center_quotas) {
        formToSend.append("multi_center_quotas", "on");
      } else {
        formToSend.append("multi_center_quotas", "off");
      }

      if (values.uses_lesson) {
        formToSend.append("lesson_module", "on");
      } else {
        formToSend.append("lesson_module", "off");
      }

      if (values.cameras_module) {
        formToSend.append("cameras_module", "on");
      } else {
        formToSend.append("cameras_module", "off");
      }
      formToSend.append("rule_penalty", String(values.rule_penalty));
      formToSend.append("rule_max", String(values.rule_max));

      if (values.uses_assisteds) {
        formToSend.append("assisted_module", "on");
      } else {
        formToSend.append("assisted_module", "off");
      }

      if (values.slots) {
        formToSend.append("slots", "on");
      } else {
        formToSend.append("slots", "off");
      }

      if (values.catchment_module) {
        formToSend.append("catchment_module", "on");
      } else {
        formToSend.append("catchment_module", "off");
      }

      if (values.recovery_module) {
        formToSend.append("recovery_module", "on");
      } else {
        formToSend.append("recovery_module", "off");
      }

      if (values.ticket_module) {
        formToSend.append("ticket_module", "on");
      } else {
        formToSend.append("ticket_module", "off");
      }

      if (values.store) {
        formToSend.append("store", "on");
      } else {
        formToSend.append("store", "off");
      }

      if (values.course_module) {
        formToSend.append("course_module", "on");
      } else {
        formToSend.append("course_module", "off");
      }

      if (values.access_module) {
        formToSend.append("access_module", "on");
      } else {
        formToSend.append("access_module", "off");
      }

      formToSend.append(
        "access_module_type",
        String(values.access_module_type)
      );
      if (values.type_branch === 1) {
        formToSend.append("type_branch", "gimnasio");
      } else {
        formToSend.append("type_branch", "entrenador_personal");
      }

      formToSend.append("contract", values.contract);

      if (values.available_marketing) {
        for (const marketing of values.available_marketing) {
          formToSend.append("available_marketing[]", marketing.id);
        }
      }

      if (values.accounting_module) {
        formToSend.append("accounting_module", "on");
      } else {
        formToSend.append("accounting_module", "off");
      }

      if (values.direct_debit_module) {
        formToSend.append("direct_debit_module", "on");
      } else {
        formToSend.append("direct_debit_module", "off");
      }

      formToSend.append("tax_authorities", String(values.tax_authorities));
      formToSend.append("gti_code", values.gti_code);
      formToSend.append("gti_user", values.gti_user);
      formToSend.append("gti_password", values.gti_password);
      formToSend.append("gti_terminal", values.gti_terminal);
      if (values.payment_card) {
        formToSend.append("payment_card", "on");
      } else {
        formToSend.append("payment_card", "off");
      }
      formToSend.append("gateway", values.gateway);
      formToSend.append("public_key_payment", values.public_key_payment);
      formToSend.append("private_key_payment", values.private_key_payment);
      formToSend.append("prod_integrity", values.prod_integrity);

      if (values.uses_subscription) {
        formToSend.append("uses_subscription", "on");
      } else {
        formToSend.append("uses_subscription", "off");
      }

      if (values.available_subscriptions) {
        for (const subscription of values.available_subscriptions) {
          formToSend.append("available_subscriptions[]", subscription.id);
        }
      }

      formToSend.append("contract_id", String(values.contract_id));

      if (values.subscription_blocking) {
        formToSend.append("subscription_blocking", "on");
      } else {
        formToSend.append("subscription_blocking", "off");
      }

      formToSend.append("trial_period_days", String(values.trial_period_days));
      for (const [key, value] of formToSend.entries()) {
        console.log(`${key}:`, value);
      }
      await updateBranch({
        branchId: id,
        body: formToSend,
      }).unwrap();
      toasts.success(
        "Filial actualizada",
        "La filial se ha actualizado correctamente"
      );

      dispatch(clearBranch());
      branchQuery.refetch();
      setBranchFormVisible(false);
    } catch (error) {
      console.error("Error on handleUpdateBranch", error);
      toasts.danger(
        "Error al actualizar",
        "Ha ocurrido un error al actualizar la filial"
      );
    }
  };

  const handleDeleteBranch = async (branch: any) => {
    try {
      await deleteBranch({ id: branch.id });
      toasts.success(
        "Filial eliminada",
        "La filial se ha eliminado correctamente"
      );
      navigate("/branchs");
      setBranchDeleteFormVisible(false);
    } catch (error) {
      console.error("Error on handleDeleteBranch", error);
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar la filial"
      );
    }
  };

  return (
    <Fragment>
      {branchQuery.isLoading || branchQuery.isFetching ? (
        <div className="flex justify-center items-center h-screen w-screen">
          <FontAwesomeIcon icon={faSpinner} spin className="text-6xl" />
        </div>
      ) : (
        <>
          <div className="flex flex-row flex-1 w-full h-full">
            <BranchSideBar
              onBranchEdit={() => setBranchFormVisible(true)}
              onBranchDelete={() => setBranchDeleteFormVisible(true)}
            />

            <div className="flex flex-col flex-1 w-0 h-full overflow-auto">
              <Outlet />
            </div>
          </div>
          {branchQuery.isSuccess && (
            <BranchForm
              visible={branchFormVisible}
              setVisible={setBranchFormVisible}
              onSubmit={handleUpdateBranch}
              gyms={gymsQuery.data ?? []}
              branch={branchQuery.data}
              countries={countriesQuery.data ?? []}
              states={statesQuery.data ?? []}
              timezones={timezonesQuery.data ?? []}
              isLoadingCreateUpdate={updateBranchResult.isLoading}
            />
          )}

          <BranchDeleteForm
            branch={branchQuery.data}
            onDelete={handleDeleteBranch}
            visible={branchDeleteFormVisible}
            setVisible={setBranchDeleteFormVisible}
          />
        </>
      )}
    </Fragment>
  );
};
